import Button from '@jetbrains/ring-ui/components/button/button'
import {Caption} from '@jetbrains/ring-ui/components/button-group/button-group'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import type {ListDataItem} from '@jetbrains/ring-ui/components/list/consts'
import PopupMenu from '@jetbrains/ring-ui/components/popup-menu/popup-menu'
import classNames from 'classnames'
import * as React from 'react'

import {getEmptyHash} from '../../../../../../../utils/empty'
import {keyValue, objectEntries, objectKeys} from '../../../../../../../utils/object'
import Popup from '../../../../../../common/Popup/Popup'
import type {Limit, LimitNames, LimitValue} from '../../Rules.types'
import {LoadingContext, DispatchContext} from '../CleanupForm.contexts'
import styles from '../CleanupForm.css'
import type {CleanupFormValidationErrors} from '../CleanupForm.types'

type OwnProps = {
  value: Limit
  setError: (arg0: CleanupFormValidationErrors) => unknown
}
export const defaultLimitValueData: Limit = {
  all: getEmptyHash(),
}
export const limitOptionCaptions = {
  all: 'All builds',
  keepNDaysSinceLastBuild: 'days before the last build',
  keepNDaysSinceLastSuccessfulBuild: 'days before the last successful build',
  keepNDaysSinceToday: 'days before today',
  keepLastNBuilds: 'last builds',
}
const popupItems = objectEntries(limitOptionCaptions).map(([name, label]) => ({
  name,
  label,
}))
const MAX_COUNT_TO_KEEP = 1000000
const DEFAULT_KEEP_VALUE = 30
const ERROR_DESCRIPTION = {
  nonZero: 'Value should be more than 0',
  nonNegative: 'Value should be more than 0',
  nonEmpty: `Value should be a number between 1 and ${MAX_COUNT_TO_KEEP}`,
  exceedLimits: `Value should not be more than ${MAX_COUNT_TO_KEEP}`,
}

const getNumberFieldName = (type: LimitNames): LimitValue =>
  ['keepNDaysSinceLastBuild', 'keepNDaysSinceLastSuccessfulBuild', 'keepNDaysSinceToday'].includes(
    type,
  )
    ? 'daysCount'
    : 'buildsCount'

function LimitsSection({value, setError}: OwnProps) {
  const disabled = React.useContext(LoadingContext)
  const currentLimitName = React.useMemo<LimitNames>(() => {
    let result: LimitNames = 'all'
    objectKeys(value).forEach(key => {
      if (value[key] != null) {
        result = key
      }
    })
    return result
  }, [value])
  const [countDaysOrBuilds, setNumber] = React.useState<number>(
    value[currentLimitName]?.daysCount ||
      value[currentLimitName]?.buildsCount ||
      DEFAULT_KEEP_VALUE,
  )
  const setValue = React.useContext(DispatchContext)
  const onChange = React.useCallback(
    (values: Limit) =>
      setValue?.({
        name: 'limit',
        value: values,
      }),
    [setValue],
  )
  const handleCountChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.checkValidity()) {
        return
      }

      const digitValue = Number(event.target.value.replace(/([^0-9])/g, ''))
      setNumber(digitValue)
      onChange(
        keyValue(currentLimitName, keyValue(getNumberFieldName(currentLimitName), digitValue)),
      )
    },
    [currentLimitName, onChange],
  )
  const handleSelect = React.useCallback(
    (option: ListDataItem<{name: LimitNames}>) => {
      onChange(
        keyValue(
          option.name,
          option.name === 'all'
            ? getEmptyHash()
            : keyValue(getNumberFieldName(option.name), countDaysOrBuilds),
        ),
      )
    },
    [countDaysOrBuilds, onChange],
  )
  const inputClasses = classNames(styles.input, styles.forSection, styles.short)
  const currentError = React.useMemo(() => {
    let error = null
    const currentCount = Number(countDaysOrBuilds)

    if (isNaN(currentCount)) {
      error = ERROR_DESCRIPTION.nonEmpty
    }

    if (currentCount === 0) {
      error = ERROR_DESCRIPTION.nonZero
    }

    if (currentCount < 0) {
      error = ERROR_DESCRIPTION.nonNegative
    }

    if (currentCount > MAX_COUNT_TO_KEEP) {
      error = ERROR_DESCRIPTION.exceedLimits
    }

    return error
  }, [countDaysOrBuilds])
  React.useEffect(() => {
    setError({
      limits: currentError,
    })
  }, [setError, currentError])
  return (
    <div className={classNames(styles.row, styles.separateRow)}>
      <Caption className={styles.caption}>{'Range'}</Caption>
      <div className={styles.content}>
        <div className={classNames(styles.inputs)}>
          {currentLimitName !== 'all' && (
            <input
              required
              type="number"
              min={1}
              max={MAX_COUNT_TO_KEEP}
              disabled={disabled}
              value={countDaysOrBuilds}
              onChange={handleCountChange}
              className={inputClasses}
            />
          )}
          <Dropdown
            anchor={
              <Button disabled={disabled} dropdown text className={styles.textButton}>
                {limitOptionCaptions[currentLimitName]}
              </Button>
            }
          >
            {!disabled ? (
              <PopupMenu onSelect={handleSelect} closeOnSelect data={popupItems} />
            ) : (
              <Popup>{''}</Popup>
            )}
          </Dropdown>
        </div>
        <div className={styles.hint}>
          {'Defines a quantity or time range for matching builds to be preserved'}
        </div>
        {currentError != null && <div className={styles.error}>{currentError}</div>}
      </div>
    </div>
  )
}

export default React.memo<OwnProps>(LimitsSection)
